import { TrashIcon } from "@phosphor-icons/react";
import { useCallback, useEffect, useState } from "react";
import KeyMessagesList from "@/components/discovery/key-message-selector/KeyMessagesList";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/feedback/skeleton";
import InlineSearchEditor from "@/components/ui/tiptap-editor/InlineSearchEditor";
import { ExpandedSearchEditor } from "@/components/ui/tiptap-editor/TiptapEditor";
import type { BroadcastKMItem } from "@/data-access/discovery-view/queries/key-message-queries";
import { useFeature } from "@/lib/feature-management";
import { addCommasToNumbers } from "@/lib/utils/prettyName";
import useDiscoveryContentManagementStore from "@/store/discovery/discovery-content-management/discovery-content-management.slice";
import { fetchBroadcastKeyMessageGroups } from "@/store/discovery/discovery-key-messages/discovery-key-message.async-actions";
import useDiscoveryKeyMessageStore from "@/store/discovery/discovery-key-messages/discovery-key-message.slice";
import { useDiscoveryContext } from "@/store/discovery/discovery-params.context";

const BroadcastClipsTabContentManagementPanel = () => {
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);

  const { feedId } = useDiscoveryContext();

  // Feature flag lives here so KeyMessagesList stays decoupled from it
  const isNavV2Stable = useFeature("navigation-v2-stable", false);

  const broadcastGroups = useDiscoveryKeyMessageStore(
    (state) => state.broadcastGroups,
  );
  const isFetchingBroadcast = useDiscoveryKeyMessageStore(
    (state) => state.isFetchingBroadcast,
  );
  const broadcastGroupsError = useDiscoveryKeyMessageStore(
    (state) => state.broadcastGroupsError,
  );
  const selectedBroadcastKeyMessageIds = useDiscoveryKeyMessageStore(
    (state) => state.selectedBroadcastKeyMessageIds,
  );
  const setSelectedBroadcastKeyMessageIds = useDiscoveryKeyMessageStore(
    (state) => state.setSelectedBroadcastKeyMessageIds,
  );

  const totalCount = useDiscoveryContentManagementStore(
    (state) => state.totalBroadcastClipsCount,
  );
  const isFetchingClips = useDiscoveryContentManagementStore(
    (state) => state.isFetchingBroadcastClips,
  );
  const searchTerm = useDiscoveryContentManagementStore(
    (state) => state.broadcastClipsSearchTerm,
  );
  const setBroadcastClipsSearchTerm = useDiscoveryContentManagementStore(
    (state) => state.setBroadcastClipsSearchTerm,
  );
  const resetBroadcastClipsTableState = useDiscoveryContentManagementStore(
    (state) => state.resetBroadcastClipsTableState,
  );
  const selectedBroadcastClipIds = useDiscoveryContentManagementStore(
    (state) => state.selectedBroadcastClipIds,
  );
  const setSelectedBroadcastClipIds = useDiscoveryContentManagementStore(
    (state) => state.setSelectedBroadcastClipIds,
  );
  const setPendingBroadcastExclusionIds = useDiscoveryContentManagementStore(
    (state) => state.setPendingBroadcastExclusionIds,
  );
  const setIsBroadcastExcludeModalOpen = useDiscoveryContentManagementStore(
    (state) => state.setIsBroadcastExcludeModalOpen,
  );

  useEffect(() => {
    if (feedId) fetchBroadcastKeyMessageGroups(String(feedId));
  }, [feedId]);

  const selectedCount = selectedBroadcastClipIds.length;

  const handleBulkDelete = useCallback(() => {
    setPendingBroadcastExclusionIds(selectedBroadcastClipIds);
    setIsBroadcastExcludeModalOpen(true);
  }, [
    selectedBroadcastClipIds,
    setPendingBroadcastExclusionIds,
    setIsBroadcastExcludeModalOpen,
  ]);

  const handleSearch = useCallback(
    (value: string) => {
      resetBroadcastClipsTableState();
      setSelectedBroadcastClipIds([]);
      setBroadcastClipsSearchTerm(value);
    },
    [
      resetBroadcastClipsTableState,
      setSelectedBroadcastClipIds,
      setBroadcastClipsSearchTerm,
    ],
  );

  const handleRetryBroadcastKM = useCallback(() => {
    if (feedId) {
      fetchBroadcastKeyMessageGroups(String(feedId));
    }
  }, [feedId]);

  const handleExpand = () => setIsSearchExpanded(true);
  const handleCollapse = () => setIsSearchExpanded(false);

  // Map store groups to the shape expected by KeyMessagesList
  const kmGroups = broadcastGroups.map((group) => ({
    id: group.id,
    name: group.name,
    items: group.broadcastKeyMessageItems.map((item: BroadcastKMItem) => ({
      id: item.id,
      name: item.name,
    })),
  }));

  return (
    <div className="space-y-2 pb-1 pt-6">
      <div className="flex flex-col items-start sm:flex-row sm:justify-between sm:items-center gap-2 px-0 pb-1">
        <KeyMessagesList
          groups={kmGroups}
          isFetching={isFetchingBroadcast}
          error={broadcastGroupsError}
          onRetry={handleRetryBroadcastKM}
          selectedIds={selectedBroadcastKeyMessageIds}
          onSelectionChange={setSelectedBroadcastKeyMessageIds}
          defaultLabel="None selected"
          triggerClassName={isNavV2Stable ? "text-violet-800" : undefined}
        />
        <div className="flex items-center gap-2">
          {selectedCount > 0 && (
            <Button
              variant="outline"
              size="sm"
              className="gap-1.5 h-[30px] px-2.5 text-xs font-normal"
              onPress={handleBulkDelete}
            >
              <TrashIcon size={14} className="text-red-600" />
              Delete ({addCommasToNumbers(selectedCount)})
            </Button>
          )}
          {!isSearchExpanded && (
            <InlineSearchEditor
              handleSearch={handleSearch}
              handleExpand={handleExpand}
              content={searchTerm}
              isSearching={isFetchingClips && !!searchTerm}
            />
          )}
        </div>
      </div>
      {isSearchExpanded && (
        <ExpandedSearchEditor
          handleSearch={handleSearch}
          handleExpand={handleCollapse}
          content={searchTerm}
          isSearching={isFetchingClips && !!searchTerm}
        />
      )}
      <div className="flex items-center justify-between gap-2 px-1.5 pt-2">
        <span className="text-xs text-gray-900">
          {isFetchingClips ? (
            <Skeleton variant="text" className="h-4 w-16" />
          ) : (
            <>
              {addCommasToNumbers(totalCount)}{" "}
              {totalCount === 1 ? "Clip" : "Clips"}
            </>
          )}
        </span>
      </div>
    </div>
  );
};

export default BroadcastClipsTabContentManagementPanel;
